/**
 * Limited HTML for Command Center report body text (email-style).
 * Keep in lockstep with erp_api/routes/controllers/reportTextHtml.ts
 */

const ALLOWED_TAGS = new Set([
  'b', 'strong', 'i', 'em', 'u', 'br', 'p', 'div', 'ul', 'ol', 'li', 'span',
]);

/** Strip tags for char counts / PDF / plain description fallback. */
export const stripReportTextHtml = (raw: string): string => (
  String(raw || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|ul|ol)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
);

const escapeText = (raw: string): string => (
  String(raw || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
);

/**
 * Allow only simple email-style tags. Drops scripts, styles, event handlers, links.
 */
export const sanitizeReportTextHtml = (raw: unknown, maxChars = 12000): string => {
  let html = String(raw || '');
  if (!html.trim()) return '';

  // Normalize newlines from plain paste into breaks when no tags present.
  if (!/<[a-z][\s\S]*>/i.test(html)) {
    html = escapeText(html).replace(/\n/g, '<br/>');
  }

  html = html
    .replace(/<\/?(script|style|iframe|object|embed|form|input|button|img|svg|link|meta)[^>]*>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/\s(href|src|style|class|id)\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '');

  html = html.replace(/<\/?([a-z0-9]+)(\s[^>]*)?>/gi, (full, tagName: string) => {
    const tag = String(tagName || '').toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) return '';
    const isClose = full.startsWith('</');
    if (tag === 'br') return '<br/>';
    if (isClose) return `</${tag}>`;
    return `<${tag}>`;
  });

  const plain = stripReportTextHtml(html);
  if (plain.length > maxChars) {
    // Hard cap by plain length — keep truncated HTML roughly under budget.
    return sanitizeReportTextHtml(plain.slice(0, maxChars), maxChars);
  }
  return html.trim();
};

/** True when the block has visible text after stripping tags. */
export const reportTextHtmlHasContent = (raw: unknown): boolean => (
  Boolean(stripReportTextHtml(String(raw || '')))
);
